import { FormHandles } from '@unform/core';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';

import { Painel } from '../../../../components/Painel';
import { InputTextArea } from '../../../../components/InputTextArea';
import { optionProps } from '../../../../components/Select';
import { SeparateLabel } from '../../../../components/SeparateLabel';
import { CardMuscleGroup } from '../../../../components/CardMuscleGroup';
import { TextInfo } from '../../../../components/TextInfo';
import { Icon } from '../../../../components/Icon';
import {
  ComingSoonTag,
  DisabledField,
} from '../../../../components/ComingSoonTag';

import { useToast } from '../../../../hooks/toast';
import { getValidationErrors } from '../../../../utils/getValidationErrors';
import { ExercisesService } from '../../../../services/ExercisesService';
import {
  IExerciseMuscleGroup,
  IMuscleGroup,
} from '../../../../domains/Exercise';
import { colors } from '../../../../styles/tokens';

import {
  Card,
  Container,
  ContainerCardsMuscleGroups,
  ContainerInline,
  DifficultyOption,
  DifficultyRow,
  Discard,
  FormContainer,
  Grid,
  Header,
  HeaderActions,
  Input,
  MediaPlaceholder,
  PageTitle,
  SelectField,
  SidebarTitle,
  Submit,
} from './styles';

interface IFormProps {
  name: string;
  muscle_groups: IExerciseMuscleGroup[];
  description: string;
}

const ExerciseCreate: React.FC = () => {
  const formRef = useRef<FormHandles>(null);
  const navigate = useNavigate();

  const { addToast } = useToast();
  const exercisesService = new ExercisesService();

  const [muscleGroups, setMuscleGroups] = useState<IMuscleGroup[]>([]);
  const [listMuscleGroups, setListMuscleGroups] = useState<
    IExerciseMuscleGroup[]
  >([]);
  const [loading, setLoading] = useState(false);

  const handleSubmit = useCallback(
    async (data: IFormProps) => {
      try {
        setLoading(true);

        if (listMuscleGroups.length < 1) {
          addToast({
            type: 'error',
            title: 'Pelo menos um grupo muscular deve ser cadastrado.',
          });
          setLoading(false);
          return;
        }

        if (!listMuscleGroups.some(m => m.is_main)) {
          addToast({
            type: 'error',
            title: 'Pelo menos um grupo muscular deve ser o principal.',
          });
          setLoading(false);
          return;
        }

        if (listMuscleGroups.filter(m => m.is_main).length > 1) {
          addToast({
            type: 'error',
            title: 'Apenas um grupo muscular pode ser o principal.',
          });
          setLoading(false);
          return;
        }

        formRef.current?.setErrors({});

        const schema = Yup.object({
          name: Yup.string().required('Campo obrigatório'),
          description: Yup.string(),
        });

        await schema.validate(data, { abortEarly: false });

        await exercisesService.createExercise({
          name: data.name,
          muscle_groups: listMuscleGroups,
          description: data.description || undefined,
        });

        addToast({
          type: 'success',
          title: 'Exercício criado com sucesso',
        });

        setLoading(false);
        navigate('/exercises');
      } catch (err: any) {
        setLoading(false);
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          formRef.current?.setErrors(errors);
          return;
        }

        if (err?.response?.status === 406) {
          formRef.current?.setErrors({
            name: 'Já existe um exercício com esse nome',
          });
          addToast({
            type: 'error',
            title: 'Exercicio com o mesmo nome ja cadastrado.',
          });
          return;
        }

        addToast({
          type: 'error',
          title: 'Erro na criação do exercício',
          description:
            'Houve um erro na criação do exercicio. Tente novamente mais tarde.',
        });
      }
    },
    [addToast, exercisesService, listMuscleGroups, navigate],
  );

  const addItemMuscleGroup = useCallback(
    (selected: optionProps) => {
      if (
        !listMuscleGroups.find(
          group => group.muscle_group_id === selected.value,
        )
      ) {
        const groupfinded = muscleGroups.find(
          finded => finded.id === selected.value,
        );
        if (groupfinded) {
          setListMuscleGroups(element => [
            ...element,
            {
              name: selected.label,
              muscle_group_id: selected.value,
              is_main: false,
            } as IExerciseMuscleGroup,
          ]);
        }
      }
    },
    [listMuscleGroups, muscleGroups],
  );

  const handleChangeSelect = useCallback(
    (e: any) => {
      const option: optionProps = e;
      if (option) {
        addItemMuscleGroup(option);
      }
    },
    [addItemMuscleGroup],
  );

  const getMuscleGroups = useCallback(() => {
    exercisesService
      .getMuscleGroups()
      .then(response => {
        setMuscleGroups(response.data);
      })
      .catch(() => {
        addToast({
          type: 'error',
          title: 'Erro na busca dos grupos musculares.',
          description:
            'Caso o problema persista, entre em contato com o administrador do sistema',
        });
      });
  }, []);

  useEffect(() => {
    getMuscleGroups();
  }, []);

  return (
    <Painel>
      <Container>
        <Header>
          <PageTitle>Novo exercício</PageTitle>
          <HeaderActions>
            <Discard type="button" onClick={() => navigate('/exercises')}>
              Descartar
            </Discard>
          </HeaderActions>
        </Header>

        <FormContainer ref={formRef} onSubmit={handleSubmit}>
          <Grid>
            <div>
              <Card>
                <SeparateLabel label="Informações gerais" />

                <ContainerInline>
                  <Input name="name" label="Nome do exercício" />
                  <SelectField
                    clearValueChange
                    name="muscle_group"
                    label="Grupo muscular"
                    options={muscleGroups.map(group => ({
                      value: group.id as string,
                      label: group.name as string,
                    }))}
                    defaultValue={{ value: '' }}
                    placeholder="Grupo muscular"
                    onChange={handleChangeSelect}
                  />
                </ContainerInline>

                {listMuscleGroups.length > 0 &&
                  !listMuscleGroups.find(mg => mg.is_main) && (
                    <TextInfo text="Clique em um grupo muscular para torná-lo principal (usado para ordenação dos exercícios)." />
                  )}

                <ContainerCardsMuscleGroups>
                  {listMuscleGroups.map(group => (
                    <CardMuscleGroup
                      key={group.id}
                      label={group?.name as string}
                      isMain={group.is_main ?? false}
                      onClickClose={() =>
                        setListMuscleGroups(groups => [
                          ...groups.filter(current => current.id !== group.id),
                        ])
                      }
                      onClickSetMain={() =>
                        setListMuscleGroups(groups => [
                          ...groups.map(current => {
                            if (current.id === group.id) {
                              // eslint-disable-next-line no-param-reassign
                              current.is_main = !current.is_main;
                            }
                            return current;
                          }),
                        ])
                      }
                    />
                  ))}
                </ContainerCardsMuscleGroups>
              </Card>

              <Card>
                <SeparateLabel label="Instruções técnicas" />
                <InputTextArea
                  name="description"
                  label="Descrição"
                  placeholder="Explique como executar o exercício"
                />

                <div>
                  <SidebarTitle>
                    Nível de dificuldade
                    <ComingSoonTag />
                  </SidebarTitle>
                  <DisabledField>
                    <DifficultyRow>
                      <DifficultyOption type="button" disabled>
                        Iniciante
                      </DifficultyOption>
                      <DifficultyOption type="button" disabled>
                        Intermediário
                      </DifficultyOption>
                      <DifficultyOption type="button" disabled>
                        Avançado
                      </DifficultyOption>
                    </DifficultyRow>
                  </DisabledField>
                </div>
              </Card>

              <Submit
                name="submit"
                value="Criar exercício"
                loading={loading}
                disabled={loading}
              />
            </div>

            <div>
              <Card>
                <SidebarTitle>Mídia</SidebarTitle>
                <MediaPlaceholder>
                  <Icon
                    name="cloud_upload"
                    size={32}
                    color={colors.onSurfaceVariant}
                  />
                  <p>
                    O upload de foto/vídeo fica disponível após criar o
                    exercício.
                  </p>
                </MediaPlaceholder>
              </Card>

              <Card>
                <SidebarTitle>
                  Equipamento necessário
                  <ComingSoonTag />
                </SidebarTitle>
                <DisabledField>
                  <SelectField
                    name="equipment"
                    options={[]}
                    placeholder="Selecionar equipamento"
                  />
                </DisabledField>
              </Card>
            </div>
          </Grid>
        </FormContainer>
      </Container>
    </Painel>
  );
};

export { ExerciseCreate };
